import { useState, type MouseEvent } from "react";
import { Link, useNavigate } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { Button } from "../../primitives/button";
import { Badge } from "../../primitives/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../primitives/dropdown-menu";
import {
  Calendar,
  CalendarPlus,
  AlertCircle,
  Zap,
  Phone,
  Mail,
  Users,
  User,
  FileText,
  Briefcase,
  FileSpreadsheet,
  Eye,
  MoreVertical,
  CheckCircle,
  CheckCheck,
  RotateCcw,
  Megaphone,
  ExternalLink,
  Building2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  formatEscalatesIn,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import {
  isOverdue as isPastDeadline,
  isDueToday as isDueTodayDeadline,
} from "@shared-core/domain/deadline";
import { campaignTicketLabel } from "@shared-core/domain/campaign-ticket-label";
import type * as API from "@shared-core/data/api-types";
import { AlertDetailModal } from "../alerts/AlertDetailModal";
import { RecommendationDetailModal } from "../recommendations/RecommendationDetailModal";
import {
  PRIORITY_CONFIG,
  STATUS_CONFIG,
  type TicketPriority,
  type TicketStatusValue,
} from "./TicketMetadataStrip";
import { useTicketCalendarAction } from "./TicketCalendarAction";
import { type TicketListColumns } from "@shared-core/domain/ticket-list-columns";

interface TicketCardProps {
  ticket: API.Ticket | API.TicketViewItem;
  customerName?: string;
  // Drops the covering/category columns, which are near-always empty for
  // campaign-raised tickets (no CSR-coverage or category concept applies to
  // them) — used by the pink "Campaign tickets" box so the row doesn't carry
  // dead-space columns that push the contact-info/timestamp columns out of
  // view. The main "All Tickets"/"My Tickets" list keeps the full, aligned
  // column set.
  compact?: boolean;
  // Narrower still, for a half-width dashboard column rather than a
  // full-width list (sales-dashboard-hub's consolidated to-do list). Drops
  // the assignee, watcher-count, "opened" and created-at columns (assignee
  // is redundant — the viewer's own tickets are always assigned to them —
  // and the rest are non-essential at a glance) and swaps the interactive
  // status Select for a plain read-only badge, since the row's fixed-width
  // columns otherwise overflow a ~450px container and bleed into whatever
  // sits to the right of it.
  dense?: boolean;
  // Which optional columns any ticket on the page fills (ticketListColumns).
  // A column no ticket uses is dropped for the whole list, so its width goes
  // to the title. Omitted, every column shows.
  columns?: TicketListColumns;
  onComplete?: () => void;
  onReopen?: () => void;
  onSaveTicket?: (updates: API.TicketUpdateRequest) => void;
}

type FetchStatus = "idle" | "loading" | "error";

const ALL_COLUMNS: TicketListColumns = {
  cover: true,
  category: true,
  campaign: true,
  contact: true,
};

export function TicketCard({
  ticket,
  customerName,
  compact = false,
  dense = false,
  columns = ALL_COLUMNS,
  onComplete,
  onReopen,
  onSaveTicket,
}: TicketCardProps) {
  const dataAdapter = useDataAdapter();
  const navigate = useNavigate();
  const isCompleted =
    ticket.status === "resolved" || ticket.status === "closed";
  const priority = ticket.priority as TicketPriority;
  const status = ticket.status as TicketStatusValue;
  const categoryName = "category_name" in ticket ? ticket.category_name : null;
  const watcherCount = "watcher_count" in ticket ? ticket.watcher_count : 0;
  // csr-ticket-routing V2: the CSR is away today (per work_visibility_module's
  // daily absence snapshot) — only present on the composed TicketViewItem.
  const assigneeAbsentToday =
    "assignee_absent_today" in ticket ? ticket.assignee_absent_today : false;
  const coveringUserName =
    "covering_user_name" in ticket ? ticket.covering_user_name : null;
  // The assignee's display name (resolved server-side on the composed
  // TicketViewItem) so the list shows who owns each ticket without opening it.
  const assigneeUserName =
    "assignee_user_name" in ticket ? ticket.assignee_user_name : null;
  // Contact email — shown alongside the company name so a rep can see who
  // they'd be calling/emailing before opening the ticket.
  const primaryContactEmail =
    "primary_contact_email" in ticket ? ticket.primary_contact_email : null;
  // "Has anyone opened this ticket yet" — stamped once, on first open, by
  // any viewer (see workflow_module's mark_ticket_viewed). Ticket-level,
  // not per-viewer.
  const isViewed =
    "first_viewed_at" in ticket && Boolean(ticket.first_viewed_at);

  // The discrete alert_id/opportunity_id columns collapsed into the generic
  // connection (subject_type, subject_id); derive the linked ids locally.
  const linkedAlertId =
    ticket.subject_type === "alert" ? (ticket.subject_id ?? null) : null;
  const linkedOpportunityId =
    ticket.subject_type === "opportunity" ? (ticket.subject_id ?? null) : null;
  const linkedJobId =
    ticket.subject_type === "job" ? (ticket.subject_id ?? null) : null;
  // subject_id holds estimate_header_ref, not the estimate's row id — the
  // ticket tracks the enquiry, resolved to whichever revision is current.
  const linkedEstimateHeaderRef =
    ticket.subject_type === "estimate" ? (ticket.subject_id ?? null) : null;
  // Campaign-raised tickets (see marketing_campaigns_module's
  // raise_engagement_tickets/raise_bounce_tickets) carry the campaign's name
  // in subject_label — shown so a rep working the pink campaign-tickets box
  // (or the main list) can tell which campaign each ticket came from without
  // opening it.
  const campaignName = campaignTicketLabel(ticket);

  // department-ticket-routing: owning_department_name/escalates_at only
  // exist on the composed TicketViewItem (not the bare Ticket) — narrowed
  // the same way as the other TicketViewItem-only fields above.
  const owningDepartmentName =
    "owning_department_name" in ticket ? ticket.owning_department_name : null;
  const escalatesAt = "escalates_at" in ticket ? ticket.escalates_at : null;
  // "Escalates in Xh" is only meaningful while the ticket is unclaimed —
  // once someone claims it (or the sweep escalates it to the lead) it has
  // an assignee and escalates_at reads null (see the mock/real adapters).
  const escalationHint =
    !ticket.assigned_to_user_id && escalatesAt
      ? formatEscalatesIn(escalatesAt)
      : null;

  // Due date handling
  const dueDate = ticket.due_date ? new Date(ticket.due_date) : null;
  const isDueToday = ticket.due_date
    ? isDueTodayDeadline(ticket.due_date)
    : false;
  const isOverdue =
    ticket.due_date && !isCompleted ? isPastDeadline(ticket.due_date) : false;

  // State for modals — alert/opportunity previews only. Ticket detail
  // itself is a page now (see TicketDetailPage), not a modal opened from
  // here, so it needs no pre-fetch state of its own.
  const [isAlertModalOpen, setAlertModalOpen] = useState(false);
  const [isOpportunityModalOpen, setOpportunityModalOpen] = useState(false);

  const calendarAction = useTicketCalendarAction(ticket);

  // Fetch-on-demand state for the alert/opportunity badge previews.
  const [fetchedAlert, setFetchedAlert] = useState<API.Alert | null>(null);
  const [alertFetchStatus, setAlertFetchStatus] = useState<FetchStatus>("idle");
  const [fetchedOpportunity, setFetchedOpportunity] =
    useState<API.Opportunity | null>(null);
  const [opportunityFetchStatus, setOpportunityFetchStatus] =
    useState<FetchStatus>("idle");

  const fetchAlert = async (): Promise<API.Alert | null> => {
    if (!linkedAlertId) return null;
    if (fetchedAlert) return fetchedAlert;
    setAlertFetchStatus("loading");
    try {
      const alert = await dataAdapter.getAlertById(linkedAlertId);
      setFetchedAlert(alert);
      setAlertFetchStatus("idle");
      return alert;
    } catch (error) {
      console.error("tickets.linked_alert.fetch_failed", {
        ticket_id: ticket.id,
        alert_id: linkedAlertId,
        exc_type: error instanceof Error ? error.name : typeof error,
        message: error instanceof Error ? error.message : String(error),
      });
      setAlertFetchStatus("error");
      return null;
    }
  };

  const fetchOpportunity = async (): Promise<API.Opportunity | null> => {
    if (!linkedOpportunityId) return null;
    if (fetchedOpportunity) return fetchedOpportunity;
    setOpportunityFetchStatus("loading");
    try {
      const opportunity =
        await dataAdapter.getOpportunityById(linkedOpportunityId);
      setFetchedOpportunity(opportunity);
      setOpportunityFetchStatus("idle");
      return opportunity;
    } catch (error) {
      console.error("tickets.linked_opportunity.fetch_failed", {
        ticket_id: ticket.id,
        opportunity_id: linkedOpportunityId,
        exc_type: error instanceof Error ? error.name : typeof error,
        message: error instanceof Error ? error.message : String(error),
      });
      setOpportunityFetchStatus("error");
      return null;
    }
  };

  const handleOpenAlertModal = async () => {
    const alert = await fetchAlert();
    if (alert) setAlertModalOpen(true);
  };

  const handleOpenOpportunityModal = async () => {
    const opportunity = await fetchOpportunity();
    if (opportunity) setOpportunityModalOpen(true);
  };

  const getActionTypeIcon = () => {
    switch (ticket.action_type) {
      case "call":
        return <Phone className="size-4 text-primary shrink-0" />;
      case "email":
        return <Mail className="size-4 text-primary shrink-0" />;
      case "meeting":
        return <Users className="size-4 text-primary shrink-0" />;
      case "general":
        return <FileText className="size-4 text-primary shrink-0" />;
      default:
        return null;
    }
  };

  const hasLinkedEntity =
    linkedAlertId ||
    linkedOpportunityId ||
    linkedJobId ||
    linkedEstimateHeaderRef;

  const ticketDetailPath = paths.ticketDetail({ id: ticket.id });

  // The whole row opens the ticket; controls nested inside it (status
  // select, linked-entity badge, overflow menu) stop propagation so they
  // don't also trigger a navigation.
  const handleRowClick = () => navigate(ticketDetailPath);
  const stopPropagation = (e: MouseEvent) => e.stopPropagation();

  // The full (non-compact) row carries enough fixed-width columns to
  // overflow the list's horizontal scroll area, unlike the narrower
  // campaign-tickets `compact` row — reps had to scroll right to reach
  // the overflow menu. Darker secondary text was a separate readability
  // complaint scoped to the same full rows (the compact campaign rows
  // weren't flagged).
  const mutedTextClass = compact
    ? "text-muted-foreground"
    : "text-foreground/70";

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleRowClick();
      }}
      // The full row's fixed-width columns can exceed the list's width, so it
      // grows to fit them (min-w-fit) and the list scrolls sideways; without
      // that the card's border and background stopped short of the content.
      className={`group flex items-center gap-3 px-4 py-2.5 border border-border rounded-lg bg-card hover:bg-surface-hover transition-colors cursor-pointer ${dense ? "flex-wrap gap-y-1.5" : compact ? "" : "min-w-fit"}`}
    >
      {getActionTypeIcon()}

      {/* The title is the one flexible column, so it needs a floor: with only
          min-w-0, the fixed-width columns beside it squeezed it to nothing on
          a laptop-width screen and the list showed no titles at all. */}
      <Link
        to={ticketDetailPath}
        className="flex-1 w-48 min-w-48 text-left hover:underline"
        title={ticket.title}
      >
        <span
          className={`block text-sm line-clamp-2 break-words ${isCompleted ? "line-through text-muted-foreground" : ""}`}
        >
          {ticket.title}
        </span>
      </Link>

      {/* Inline status — the five-state lifecycle is directly reachable
          from the list, not just via Mark as Resolved/Reopen. Dense mode swaps
          this for a read-only badge to save width — change status by
          opening the ticket instead. */}
      {/* Dense (dashboard) rows are too narrow for every chip on one line —
          half the page width — so the status/priority/campaign/customer
          chips drop to a second line under the title instead of spilling
          into the neighbouring column. `contents` leaves the full list's
          single-line layout untouched. */}
      <div
        className={
          dense
            ? "order-last basis-full flex flex-wrap items-center gap-x-3 gap-y-1 pl-7"
            : "contents"
        }
      >
        <div onClick={stopPropagation} className="shrink-0">
          {dense ? (
            <Badge variant="outline" className="whitespace-nowrap">
              {STATUS_CONFIG[status].label}
            </Badge>
          ) : (
            <Select
              value={status}
              onValueChange={(v) =>
                onSaveTicket?.({ status: v as TicketStatusValue })
              }
            >
              <SelectTrigger className="h-7 text-xs w-[112px]" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(STATUS_CONFIG) as TicketStatusValue[]).map(
                  (s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_CONFIG[s].label}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Fixed-width columns from here on so rows line up like a table
          regardless of how long any one row's badge text/customer name is. */}
        <div className="w-[76px] shrink-0">
          <Badge variant={PRIORITY_CONFIG[priority].variant}>
            {PRIORITY_CONFIG[priority].label}
          </Badge>
        </div>

        {/* Assignee — shown next to the priority so the list makes it obvious
          who owns each ticket without opening it. Redundant in dense mode:
          a dashboard to-do list only ever shows the viewer's own tickets. */}
        {!dense && (
          <div
            className="w-[120px] shrink-0"
            title={assigneeUserName ?? "Unassigned"}
          >
            <span
              className={`flex items-center gap-1 text-xs ${
                assigneeUserName
                  ? mutedTextClass
                  : "text-muted-foreground/60 italic"
              }`}
            >
              <User className="size-3 shrink-0" />
              <span className="truncate">
                {assigneeUserName ?? "Unassigned"}
              </span>
            </span>
          </div>
        )}

        {!compact && columns.cover && (
          <div className="w-[110px] shrink-0 hidden sm:block">
            {assigneeAbsentToday && !coveringUserName && (
              <Badge variant="destructive" className="whitespace-nowrap">
                Away, no cover
              </Badge>
            )}
            {coveringUserName && (
              <Badge
                variant="outline"
                className="whitespace-nowrap max-w-full"
                title={`Covered by ${coveringUserName}`}
              >
                <span className="truncate">Covered by {coveringUserName}</span>
              </Badge>
            )}
          </div>
        )}

        {!compact && columns.category && (
          <div className="w-[160px] shrink-0 hidden sm:block">
            {categoryName && (
              <Badge variant="outline" className="max-w-full">
                <span className="truncate">{categoryName}</span>
              </Badge>
            )}
          </div>
        )}

        {(dense ? Boolean(campaignName) : columns.campaign) && (
          <div
            className={
              dense
                ? "min-w-0 max-w-[12rem]"
                : "w-[150px] shrink-0 hidden lg:block"
            }
          >
            {campaignName && (
              <Badge
                variant="outline"
                className="max-w-full gap-1 border-pink-200 dark:border-pink-800 text-pink-700 dark:text-pink-300"
                title={campaignName}
              >
                <Megaphone className="size-3 shrink-0" />
                <span className="truncate">{campaignName}</span>
              </Badge>
            )}
          </div>
        )}

        {/* Owning department + "escalates in Xh" (department-ticket-routing)
          — surfaces escalation risk before it fires, the cheapest way to
          make escalation rare (brief §11). Only ever set for a ticket that
          couldn't reach an individual, so this column is empty for the
          overwhelming majority of rows. */}
        {!compact && owningDepartmentName && (
          <div className="w-[150px] shrink-0 hidden xl:block">
            <Badge
              variant="outline"
              className="max-w-full gap-1"
              title={`Owned by ${owningDepartmentName}`}
            >
              <Building2 className="size-3 shrink-0" />
              <span className="truncate">{owningDepartmentName}</span>
            </Badge>
            {escalationHint && (
              <div
                className={`text-[11px] mt-0.5 truncate ${
                  escalationHint === "escalation overdue"
                    ? "text-destructive"
                    : "text-foreground/70"
                }`}
              >
                {escalationHint}
              </div>
            )}
          </div>
        )}

        {columns.contact && (
          <div
            className={
              dense ? "min-w-0 max-w-[12rem]" : "w-36 shrink-0 hidden md:block"
            }
            onClick={stopPropagation}
          >
            {customerName && (
              <span className={`text-xs truncate block ${mutedTextClass}`}>
                {customerName}
              </span>
            )}
            {!dense && primaryContactEmail && (
              <a
                href={`mailto:${primaryContactEmail}`}
                title={primaryContactEmail}
                className={`text-xs truncate block hover:text-primary hover:underline ${compact ? "text-muted-foreground/80" : "text-foreground/60"}`}
              >
                {primaryContactEmail}
              </a>
            )}
          </div>
        )}
      </div>

      {!dense && (
        <div
          className="w-6 shrink-0 hidden sm:flex justify-center"
          title={isViewed ? "Opened" : "Not opened yet"}
        >
          {isViewed && <CheckCheck className={`size-3.5 ${mutedTextClass}`} />}
        </div>
      )}

      {!compact && (
        <div className="w-9 shrink-0 flex justify-center">
          {hasLinkedEntity && (
            <button
              onClick={(e) => {
                stopPropagation(e);
                if (linkedAlertId) {
                  handleOpenAlertModal();
                } else if (linkedOpportunityId) {
                  handleOpenOpportunityModal();
                } else {
                  navigate(ticketDetailPath);
                }
              }}
              disabled={
                (!!linkedAlertId && alertFetchStatus === "loading") ||
                (!!linkedOpportunityId && opportunityFetchStatus === "loading")
              }
              className="disabled:opacity-60"
              title={
                linkedAlertId
                  ? "Linked to alert"
                  : linkedOpportunityId
                    ? "Linked to opportunity"
                    : linkedJobId
                      ? "Linked to job"
                      : "Linked to estimate"
              }
            >
              <Badge variant="outline" className="gap-1">
                {linkedAlertId && <AlertCircle className="size-3" />}
                {linkedOpportunityId && <Zap className="size-3" />}
                {linkedJobId && <Briefcase className="size-3" />}
                {linkedEstimateHeaderRef && (
                  <FileSpreadsheet className="size-3" />
                )}
              </Badge>
            </button>
          )}
        </div>
      )}

      {!dense && (
        <div className="w-8 shrink-0">
          {watcherCount > 0 && (
            <span
              className={`flex items-center gap-0.5 text-xs ${mutedTextClass}`}
            >
              <Eye className="size-3" />
              {watcherCount}
            </span>
          )}
        </div>
      )}

      <div className={`${dense ? "w-[84px]" : "w-[100px]"} shrink-0`}>
        {dueDate && !isCompleted ? (
          <Badge
            variant={
              isOverdue ? "destructive" : isDueToday ? "default" : "outline"
            }
            className="gap-1 whitespace-nowrap"
          >
            <Calendar className="size-3" />
            {isOverdue
              ? formatShortDateOrDash(dueDate)
              : isDueToday
                ? "Today"
                : formatShortDateOrDash(dueDate)}
          </Badge>
        ) : !isCompleted ? (
          <span className={`text-xs whitespace-nowrap ${mutedTextClass}`}>
            No due date
          </span>
        ) : null}
      </div>

      {!dense && (
        <div className="w-24 shrink-0 hidden lg:block">
          <span className={`text-xs whitespace-nowrap ${mutedTextClass}`}>
            {formatDistanceToNow(new Date(ticket.created_at), {
              addSuffix: true,
            })}
          </span>
        </div>
      )}

      {/* Pinned to the visible right edge of the row's horizontal scroll
          area on the full (non-compact) list — this row carries enough
          fixed-width columns to overflow, unlike the narrower campaign
          `compact` row, and reps had to scroll right to reach it. */}
      <div
        onClick={stopPropagation}
        className={
          compact
            ? "shrink-0"
            : "shrink-0 sticky right-0 z-10 -mr-4 pr-4 pl-2 bg-card group-hover:bg-surface-hover rounded-r-lg"
        }
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="size-7">
              <MoreVertical className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {!isCompleted && onComplete && (
              <DropdownMenuItem onClick={onComplete}>
                <CheckCircle className="size-4" />
                Mark as Resolved
              </DropdownMenuItem>
            )}
            {isCompleted && onReopen && (
              <DropdownMenuItem onClick={onReopen}>
                <RotateCcw className="size-4" />
                Reopen Ticket
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={calendarAction.trigger}>
              {calendarAction.isOnCalendar ? (
                <ExternalLink className="size-4" />
              ) : (
                <CalendarPlus className="size-4" />
              )}
              {calendarAction.label}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Modals */}
      {isAlertModalOpen && fetchedAlert && (
        <AlertDetailModal
          alert={fetchedAlert}
          onClose={() => setAlertModalOpen(false)}
        />
      )}

      {isOpportunityModalOpen && fetchedOpportunity && (
        <RecommendationDetailModal
          recommendation={fetchedOpportunity}
          onClose={() => setOpportunityModalOpen(false)}
        />
      )}

      {calendarAction.dialogs}
    </div>
  );
}
